import { useMutation } from "@apollo/client";
import { gql } from "generated-graphql";
import { ScoutDataChangeStatus } from "generated-graphql/graphql";
import { useCallback } from "react";

/**
 * Accept / reject a staged Scout data-change proposal, or undo Applied rows.
 *
 * Confirm/decline set the durable ScoutDataChange row's status (CONFIRMED /
 * DECLINED), which is now apply-authority: the follow-up execute_graphql apply
 * reads the row, not a regex scan of the synthetic `[scout-internal-event]`
 * message. Undo reverses Applied rows the client names. Oak calls these BEFORE
 * sending the synth message so Scout only narrates.
 */

const CONFIRM_DATA_CHANGE = gql(`
  mutation ConfirmScoutDataChange($input: ConfirmScoutDataChangeInput!) {
    confirmScoutDataChange(input: $input) {
      changeId
      status
    }
  }
`);

const DECLINE_DATA_CHANGE = gql(`
  mutation DeclineScoutDataChange($input: DeclineScoutDataChangeInput!) {
    declineScoutDataChange(input: $input) {
      changeId
      status
    }
  }
`);

const UNDO_DATA_CHANGE = gql(`
  mutation UndoScoutDataChange($input: UndoScoutDataChangeInput!) {
    undoScoutDataChange(input: $input) {
      changeId
      status
      failureCategory
      failureReason
    }
  }
`);

/** Undo can take ~30s; Apollo's default (~30s) would abort it. */
export const UNDO_SCOUT_DATA_CHANGE_TIMEOUT_MS = 120_000;

export type ResolveScoutDataChangeInput = {
  changeId: string;
};

export type UndoScoutDataChangeInput = {
  changeIds: string[];
};

export type ScoutDataChangeMutationResult = {
  changeId: string;
  status: ScoutDataChangeStatus;
  failureCategory?: string | null;
  failureReason?: string | null;
};

export function useScoutDataChangeMutations() {
  const [confirmMutation] = useMutation(CONFIRM_DATA_CHANGE);
  const [declineMutation] = useMutation(DECLINE_DATA_CHANGE);
  const [undoMutation] = useMutation(UNDO_DATA_CHANGE);

  const confirmDataChange = useCallback(
    async (input: ResolveScoutDataChangeInput) => {
      await confirmMutation({ variables: { input } });
    },
    [confirmMutation]
  );

  const declineDataChange = useCallback(
    async (input: ResolveScoutDataChangeInput) => {
      await declineMutation({ variables: { input } });
    },
    [declineMutation]
  );

  const undoDataChange = useCallback(
    async (input: UndoScoutDataChangeInput): Promise<ScoutDataChangeMutationResult[]> => {
      const result = await undoMutation({
        variables: { input },
        context: {
          fetchOptions: { signal: AbortSignal.timeout(UNDO_SCOUT_DATA_CHANGE_TIMEOUT_MS) },
        },
      });
      return result.data?.undoScoutDataChange ?? [];
    },
    [undoMutation]
  );

  return { confirmDataChange, declineDataChange, undoDataChange };
}
